import { useEffect, useSyncExternalStore } from 'react';
import { resolvedEventBookingType, TDR } from 'tdr-common';
import { buildEventBookNowUrl } from '../helpers/buildEventBookNowUrl';
import {
	getCachedEventBookNow,
	getEventBookNowCacheKey,
	isEventBookNowCached,
	setCachedEventBookNow,
	subscribeEventBookNowCache
} from '../helpers/eventBookNowCache';
import { useEventAvailability } from './useEventAvailability';

export type EventBookNowResult = {
	bookNowHref: string | undefined;
	isResolved: boolean;
	isLoading: boolean;
	hasNoAvailability: boolean;
};

type UseEventBookNowOptions = {
	enabled?: boolean;
};

export function useEventBookNow(
	event: TDR.Event,
	restaurantSlug: string,
	tables: TDR.Table[],
	restaurantId: string,
	timezone?: string | null,
	options?: UseEventBookNowOptions
): EventBookNowResult {
	const enabled = options?.enabled !== false;
	const isTableEvent = resolvedEventBookingType(event) === 'table' && !!event.linkedTableSlug;
	const tableHref =
		enabled && isTableEvent && restaurantSlug
			? `/${restaurantSlug}/explore/${event.linkedTableSlug}`
			: undefined;

	const cacheKey = getEventBookNowCacheKey(restaurantId, event.id);
	const cachedEntry = useSyncExternalStore(
		subscribeEventBookNowCache,
		() => getCachedEventBookNow(cacheKey),
		() => getCachedEventBookNow(cacheKey)
	);
	const isCached = isEventBookNowCached(cacheKey);
	const hasCachedHref = isCached && cachedEntry?.bookNowHref !== null;

	// Table events skip availability — guests pick their own params on the table page.
	const availability = useEventAvailability(
		event,
		tables,
		restaurantId,
		timezone,
		!enabled || hasCachedHref || isTableEvent
	);

	useEffect(() => {
		if (isTableEvent || hasCachedHref || !enabled || !restaurantSlug) {
			return;
		}
		if (availability.isLoading) {
			return;
		}

		const href = availability.hasNoAvailability
			? null
			: buildEventBookNowUrl(
				event,
				restaurantSlug,
				tables,
				availability.firstDate,
				availability.firstTime
			) ?? null;

		setCachedEventBookNow(cacheKey, { bookNowHref: href });
	}, [
		isTableEvent,
		hasCachedHref,
		enabled,
		restaurantSlug,
		cacheKey,
		availability.isLoading,
		availability.hasNoAvailability,
		availability.firstDate,
		availability.firstTime,
		event,
		tables
	]);

	if (tableHref) {
		return {
			bookNowHref: tableHref,
			isResolved: true,
			isLoading: false,
			hasNoAvailability: false
		};
	}

	if (hasCachedHref && cachedEntry) {
		return {
			bookNowHref: cachedEntry.bookNowHref ?? undefined,
			isResolved: true,
			isLoading: false,
			hasNoAvailability: false
		};
	}

	const bookNowHref =
		!availability.isLoading && !availability.hasNoAvailability
			? buildEventBookNowUrl(
				event,
				restaurantSlug,
				tables,
				availability.firstDate,
				availability.firstTime
			)
			: undefined;

	return {
		bookNowHref,
		isResolved: !availability.isLoading,
		isLoading: availability.isLoading,
		hasNoAvailability: !availability.isLoading && availability.hasNoAvailability
	};
}
